// @flow
import * as React from 'react';
import deepEqual from 'react-fast-compare';
import { sprintf } from 'sprintf-js';
import { connect } from 'react-redux';
import scrollIntoView from 'utils/scrollIntoView';
import {
  removeArrayIndex,
  wrapPromise,
  moveArrayItem,
  objectEmpty,
  arrToObject,
  filterObject,
  mapObject,
  remove,
  uuid
} from 'utils/functional';
import styled from 'styled-components';
import ShowConfirmationModal from 'containers/ShowConfirmationModal';
import Box from 'components/Box';
import Video from 'components/Video';
import CmsIcon from 'components/CmsIcon';
import { H3, H4, HeadingSmall, Body } from 'components/Text';
import Divider from 'components/Divider';
import {
  RichtextBlock,
  ImageBlock,
  BlockquoteBlock,
  GradCtaHeroBlock,
  CtaHeroBlock,
  StickyHeaderLinksGradBlock,
  StickyHeaderLinksBlock,
  AccordionBlock,
  AnchorBlock,
  ThreeStepCtaBlock,
  VideoBlock,
  TwoTilesBlock,
  FreeCodeBlock,
  EmailCampaignSignupSingleBlock,
  EmailCampaignSignupDoubleBlock,
  FormBuilderBlock,
  ThreeSectionSliderBlock,
  CtaPromoBlock,
  FourArticlesHighlightBlock,
  RandomGradProgramsBlock,
  RecommendedActionsBlock,
  CategoryHighlightBlock,
  IdealistDaysMapBlock,
  SplitContentBlock,
  ImageGalleryBlock,
  FlashCardQuizBlock,
  PersonaQuizBlock,
  TileGridBlock,
  SupportContactFormBlock,
  ScheduledContentBlock,
  DonateBlock,
  GradFairGradProgramLinksBlock
} from 'components/Cms/Form';
import Button, { ExpandButton, DeleteButton } from 'components/Button';
import Icon from 'components/Icon';
import { FilestackImage } from 'components/Image';
import Tile, { TileHeader, TileBody } from 'components/Tile';
import {
  SortableContainer,
  SortableElement,
  SortableHandle
} from 'react-sortable-hoc';
import type { BlockType, PageType } from 'api/cms';
import TabContext from 'containers/TabContext';
import { transition, colors, gutterWidth, fonts } from '../theme';
import { showModal as showModalRaw } from '../redux/main/actions/modal';

const { useMemo, useState, useEffect, useCallback } = React;

const BLOCK_TYPES: { [BlockType]: Object } = {
  RICHTEXT: RichtextBlock,
  IMAGE: ImageBlock,
  BLOCKQUOTE: BlockquoteBlock,
  GRAD_CTA_HERO: GradCtaHeroBlock,
  CTA_HERO: CtaHeroBlock,
  STICKY_HEADER_LINKS_GRAD: StickyHeaderLinksGradBlock,
  STICKY_HEADER_LINKS: StickyHeaderLinksBlock,
  ACCORDION: AccordionBlock,
  ANCHOR: AnchorBlock,
  THREE_STEP_CTA: ThreeStepCtaBlock,
  VIDEO: VideoBlock,
  TWO_TILES: TwoTilesBlock,
  FREE_CODE: FreeCodeBlock,
  EMAIL_CAMPAIGN_SIGNUP_SINGLE: EmailCampaignSignupSingleBlock,
  EMAIL_CAMPAIGN_SIGNUP_DOUBLE: EmailCampaignSignupDoubleBlock,
  FORM_BUILDER: FormBuilderBlock,
  THREE_SECTION_SLIDER: ThreeSectionSliderBlock,
  FLASH_CARD_QUIZ: FlashCardQuizBlock,
  PERSONA_QUIZ: PersonaQuizBlock,
  CTA_PROMO: CtaPromoBlock,
  FOUR_ARTICLES_HIGHLIGHT: FourArticlesHighlightBlock,
  RANDOM_GRAD_PROGRAMS: RandomGradProgramsBlock,
  RECOMMENDED_ACTIONS: RecommendedActionsBlock,
  CATEGORY_HIGHLIGHT: CategoryHighlightBlock,
  IDEALIST_DAYS_MAP: IdealistDaysMapBlock,
  SPLIT_CONTENT: SplitContentBlock,
  IMAGE_GALLERY: ImageGalleryBlock,
  SCHEDULED_CONTENT: ScheduledContentBlock,
  SUPPORT_CONTACT_FORM: SupportContactFormBlock,
  TILE_GRID: TileGridBlock,
  DONATE: DonateBlock,
  GRAD_FAIR_GRAD_PROGRAM_LINKS: GradFairGradProgramLinksBlock
};

const blockDefaultData = (b: BlockType, subsiteId: ?string) => {
  const defaultData = {
    RICHTEXT: { html: '' },
    IMAGE: { image: null, width: 'WIDE', url: '' },
    BLOCKQUOTE: { quote: '', cite: null },
    GRAD_CTA_HERO: { image: null, text: '' },
    CTA_HERO: { image: null, text: '', buttonText: '', buttonUrl: '' },
    STICKY_HEADER_LINKS_GRAD: {
      links: [{ heading: '', subHeading: '', url: '' }]
    },
    STICKY_HEADER_LINKS: {
      links: [{ heading: '', subHeading: '', buttonText: '', buttonUrl: '' }]
    },
    ACCORDION: {
      default: 'ALL_CLOSED',
      heading: '',
      sections: [{ title: '', content: '' }]
    },
    ANCHOR: { anchor: '' },
    THREE_STEP_CTA: {
      mainHeadline: '',
      mainText: '',
      buttonText: '',
      buttonUrl: '',
      imageSize: '',
      stepOneImage: null,
      stepOneHeadline: '',
      stepOneText: '',
      stepOneUrl: '',
      stepTwoImage: null,
      stepTwoHeadline: '',
      stepTwoText: '',
      stepTwoUrl: '',
      stepThreeImage: null,
      stepThreeHeadline: '',
      stepThreeText: '',
      stepThreeUrl: ''
    },
    VIDEO: { headline: '', videoUrl: '', width: 'WIDE' },
    TWO_TILES: {
      tileOneHeadline: '',
      tileOneText: '',
      tileOneButtonText: '',
      tileOneButtonUrl: '',
      tileTwoHeadline: '',
      tileTwoText: '',
      tileTwoButtonText: '',
      tileTwoButtonUrl: ''
    },
    FREE_CODE: { code: '', isVisible: true },
    EMAIL_CAMPAIGN_SIGNUP_SINGLE: {
      headline: '',
      buttonText: '',
      campaignId: '',
      listId: ''
    },
    EMAIL_CAMPAIGN_SIGNUP_DOUBLE: {
      headline: '',
      toggleTextOne: '',
      buttonTextOne: '',
      campaignIdOne: '',
      listIdOne: '',
      toggleTextTwo: '',
      buttonTextTwo: '',
      campaignIdTwo: '',
      listIdTwo: ''
    },
    FORM_BUILDER: {
      submitButtonTextOne: '',
      method: 'POST',
      fields: [{ type: 'TEXT', options: [{ label: '' }] }]
    },
    THREE_SECTION_SLIDER: {
      headline: '',
      sections: [{ image: null, content: '' }]
    },
    CTA_PROMO: {
      headline: '',
      text: '',
      image: null,
      primaryButtonText: '',
      secondaryButtonText: '',
      secondaryButtonUrl: ''
    },
    FOUR_ARTICLES_HIGHLIGHT: {
      subsiteId,
      headline: '',
      useRecentArticles: false,
      pageOneId: '',
      pageOneTitle: '',
      pageOneImage: null,
      pageTwoId: '',
      pageTwoTitle: '',
      pageTwoImage: null,
      pageThreeId: '',
      pageThreeTitle: '',
      pageThreeImage: null,
      pageFourId: '',
      pageFourTitle: '',
      pageFourImage: null,
      buttonUrl: '',
      buttonText: ''
    },
    RANDOM_GRAD_PROGRAMS: {
      headline: ''
    },
    RECOMMENDED_ACTIONS: {
      headline: ''
    },
    CATEGORY_HIGHLIGHT: {
      headline: '',
      categories: [
        {
          id: '',
          description: '',
          image: null,
          pageIds: []
        }
      ],
      pages: []
    },
    IDEALIST_DAYS_MAP: {
      mapId: '',
      mapType: 'DAYS'
    },
    SPLIT_CONTENT: {
      appearance: 'SIDE_BY_SIDE',
      sections: [
        {
          type: 'IMAGE',
          data: {
            image: null
          }
        },
        {
          type: 'TEXT',
          data: {
            headline: null,
            html: '',
            button: null
          }
        }
      ]
    },
    IMAGE_GALLERY: {
      images: []
    },
    FLASH_CARD_QUIZ: {
      cards: [{ instructions: '', image: null, question: '', answer: '' }]
    },
    PERSONA_QUIZ: {
      results: [
        { title: '', description: '', image: null, socialMediaText: '' }
      ],
      questions: [{ title: '', answers: [{ title: '', points: [] }] }]
    },
    SUPPORT_CONTACT_FORM: {},
    SCHEDULED_CONTENT: { queueId: '' },
    TILE_GRID: {
      sections: [
        {
          tiles: [
            {
              heading: '',
              subHeading: null,
              description: '',
              thumbnail: null,
              image: null
            }
          ]
        }
      ]
    },
    DONATE: {
      headline: '', // RichText
      body: '', // RichText
      thankYouHeadline: '', // RichText
      thankYouBody: '', // RichText
      campaignId: '',
      bgImage: null
    },
    GRAD_FAIR_GRAD_PROGRAM_LINKS: {
      fairId: null,
      locationId: null
    }
  };
  return defaultData[b];
};

const PAGE_BLOCK_OPTIONS: { [BlockType]: string } = {
  /* TRANSLATORS: Content block types */
  RICHTEXT: getText('Rich Text'),
  IMAGE: getText('Image'),
  SPLIT_CONTENT: getText('Split Content'),
  BLOCKQUOTE: getText('Block Quote'),
  IMAGE_GALLERY: getText('Image Gallery'),
  CTA_HERO: getText('Call-to-Action Hero'),
  GRAD_CTA_HERO: getText('Call-to-Action Hero (Grad Program)'),
  STICKY_HEADER_LINKS: getText('Sticky Header Links'),
  STICKY_HEADER_LINKS_GRAD: getText('Sticky Header Links (Grad Program)'),
  ACCORDION: getText('Accordion'),
  ANCHOR: getText('Anchor'),
  THREE_STEP_CTA: getText('Three Step Call-to-Action'),
  VIDEO: getText('Video'),
  TWO_TILES: getText('Two Tiles'),
  FREE_CODE: getText('Code / HTML'),
  FORM_BUILDER: getText('Form Builder'),
  THREE_SECTION_SLIDER: getText('Three Section Slider'),
  FOUR_ARTICLES_HIGHLIGHT: getText('Four Articles Highlight'),
  RANDOM_GRAD_PROGRAMS: getText('Related Grad Programs'),
  RECOMMENDED_ACTIONS: getText('Recent Actions'),
  CATEGORY_HIGHLIGHT: getText('Category Highlight'),
  IDEALIST_DAYS_MAP: getText('Idealist Map'),
  FLASH_CARD_QUIZ: getText('Flash Card Quiz'),
  PERSONA_QUIZ: getText('Persona Quiz'),
  SCHEDULED_CONTENT: getText('Scheduled Content'),
  SUPPORT_CONTACT_FORM: getText('Support Contact Form'),
  TILE_GRID: getText('Tile Grid'),
  DONATE: getText('Donate'),
  GRAD_FAIR_GRAD_PROGRAM_LINKS: getText('Grad Fair Program Links'),
  EMAIL_CAMPAIGN_SIGNUP_SINGLE: getText('Email Campaign Signup - Single'),
  EMAIL_CAMPAIGN_SIGNUP_DOUBLE: getText('Email Campaign Signup - Double')
};

const SIDEBAR_BLOCK_OPTIONS: { [BlockType]: string } = {
  /* TRANSLATORS: Content block types */
  RICHTEXT: getText('Rich Text'),
  EMAIL_CAMPAIGN_SIGNUP_SINGLE: getText('Email Campaign Signup - Single'),
  EMAIL_CAMPAIGN_SIGNUP_DOUBLE: getText('Email Campaign Signup - Double')
};

const SCHEDULED_CONTENT_BLOCK_OPTIONS: { [BlockType]: string } = {
  /* TRANSLATORS: Content block types */
  SPLIT_CONTENT: getText('Split Content')
};

const getSplitBlockPreview = ({ data, type }) => {
  switch (type) {
    case 'IMAGE':
      return {
        type: 'IMAGE',
        data: data.image
      };
    case 'VIDEO':
      return {
        type: 'VIDEO',
        data: data.url
      };
    case 'BLOCKQUOTE':
      return {
        type: 'HTML',
        data: data.quote
      };
    case 'TEXT':
    default:
      return {
        type: 'HTML',
        data: data.html
      };
  }
};

const getPreview = (type, data) => {
  switch (type) {
    case 'IMAGE':
      return {
        type: 'IMAGE',
        data: data.image
      };
    case 'BLOCKQUOTE':
      return {
        type: 'HTML',
        data: data.quote
      };
    case 'GRAD_CTA_HERO':
    case 'CTA_HERO':
      return {
        type: 'HTML',
        data: data.text
      };
    case 'STICKY_HEADER_LINKS_GRAD':
    case 'STICKY_HEADER_LINKS':
      return data.links.map(link => ({ type: 'HTML', data: link.heading }));
    case 'ACCORDION':
      return data.headline
        ? {
            type: 'HTML',
            data: data.headline
          }
        : data.sections.map(section => ({ type: 'HTML', data: section.title }));
    case 'ANCHOR':
      return {
        type: 'HTML',
        data: data.anchor
      };
    case 'VIDEO':
      return {
        type: 'VIDEO',
        data: data.videoUrl
      };
    case 'THREE_STEP_CTA':
      return {
        type: 'HTML',
        data: data.mainHeadline || data.mainText || data.stepOneHeadline || ''
      };
    case 'TWO_TILES':
      return ['One', 'Two'].map(tile => ({
        type: 'HTML',
        data: data[`tile${tile}Headline`]
      }));
    case 'FREE_CODE':
      return {
        type: 'TEXT',
        data: data.code
      };
    case 'EMAIL_CAMPAIGN_SIGNUP_SINGLE':
    case 'EMAIL_CAMPAIGN_SIGNUP_DOUBLE':
    case 'FORM_BUILDER':
      return {
        type: 'TEXT',
        data: data.headline || getText('Form')
      };
    case 'THREE_SECTION_SLIDER':
      return data.headline
        ? {
            type: 'HTML',
            data: data.headline
          }
        : data.sections.map(section => ({
            type: 'HTML',
            data: section.content
          }));
    case 'FOUR_ARTICLES_HIGHLIGHT':
      return data.headline
        ? {
            type: 'HTML',
            data: data.headline
          }
        : ['One', 'Two', 'Three', 'Four'].map(page => ({
            type: 'HTML',
            data: data[`page${page}Title`]
          }));
    case 'RICHTEXT':
      return {
        type: 'HTML',
        data: data.html
      };
    case 'RANDOM_GRAD_PROGRAMS':
      return {
        type: 'TEXT',
        data: data.headline || getText('Related Grad Programs')
      };
    case 'RECOMMENDED_ACTIONS':
      return {
        type: 'TEXT',
        data: data.headline || getText('Recent Actions')
      };
    case 'CATEGORY_HIGHLIGHT':
      return data.headline
        ? {
            type: 'TEXT',
            data: data.headline
          }
        : data.categories && data.categories.length > 0
        ? data.categories.map(category => ({
            type: 'TEXT',
            data: category.description
          }))
        : {
            type: 'TEXT',
            data: getText('Category Highlight')
          };
    case 'IDEALIST_DAYS_MAP':
      return {
        type: 'TEXT',
        data: `${
          data.mapType === 'DAYS' ? 'Idealist Days' : 'Groups'
        } Map for ${data.mapId}`
      };
    case 'SPLIT_CONTENT':
      return [
        getSplitBlockPreview(data.sections[0]),
        getSplitBlockPreview(data.sections[1])
      ];
    case 'IMAGE_GALLERY':
      return {
        type: 'IMAGE',
        data: data.images.length > 0 ? data.images[0] : null
      };
    case 'FLASH_CARD_QUIZ':
      return data.cards.map(c => ({ type: 'HTML', data: c.question }));
    case 'SUPPORT_CONTACT_FORM':
      return {
        type: 'TEXT',
        data: getText('Support Contact Form')
      };
    case 'SCHEDULED_CONTENT':
      return {
        type: 'TEXT',
        data: getText('Scheduled Content')
      };
    case 'TILE_GRID':
      return data.sections.map(s => ({ type: 'HTML', data: s.name }));
    case 'DONATE':
      return { type: 'HTML', data: data.headline };
    case 'GRAD_FAIR_GRAD_PROGRAM_LINKS':
      return [
        { type: 'TEXT', data: data.fairId },
        { type: 'TEXT', data: data.locationId }
      ];
    default:
      return {
        type: 'TEXT',
        data: ''
      };
  }
};

const Preview = ({ value, type }: { value: Object, type: BlockType }) => {
  const blockPreview = getPreview(type, value.data);
  const previews =
    blockPreview instanceof Array ? blockPreview : [blockPreview];
  return (
    <>
      {previews.map((preview, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={index}>
          {index > 0 && (
            <Box mx={12} color={colors.silver}>
              |
            </Box>
          )}
          {preview.type === 'HTML' ? (
            <PreviewText
              flex={index < previews.length - 1 ? '0 1 auto' : '1 1 auto'}
              dangerouslySetInnerHTML={{
                __html: preview.data
              }}
              opacity="0.66"
            />
          ) : preview.type === 'TEXT' ? (
            <PreviewText
              flex={index < previews.length - 1 ? '0 1 auto' : '1 1 auto'}
              opacity="0.66"
            >
              {preview.data}
            </PreviewText>
          ) : preview.type === 'IMAGE' ? (
            <Box
              position="relative"
              display="block"
              height="100%"
              width={48}
              flex={index < previews.length - 1 ? '0 0 auto' : '1 0 auto'}
            >
              <Box
                position="absolute"
                top="50%"
                left={0}
                transform="translateY(-50%)"
                display="block"
              >
                {preview.data && preview.data.filename && (
                  <FilestackImage
                    alt={getText('Content block preview image')}
                    data={preview.data}
                    width={48}
                    height={48}
                    round
                    fit="crop"
                  />
                )}
              </Box>
            </Box>
          ) : preview.type === 'VIDEO' ? (
            <Box
              position="relative"
              display="block"
              height="100%"
              width={48}
              flex={index < previews.length - 1 ? '0 0 auto' : '1 0 auto'}
            >
              <Box
                position="absolute"
                top="50%"
                left={0}
                transform="translateY(-50%)"
                display="block"
                width={48}
                height={48}
                overflow="hidden"
                borderRadius="50%"
              >
                <Box width={86} height={48} transform="translateX(-19px)">
                  <Video
                    url={preview.data}
                    prefix="block"
                    noPlayer
                    hideButton
                  />
                </Box>
              </Box>
            </Box>
          ) : (
            ''
          )}
        </React.Fragment>
      ))}
    </>
  );
};

const PreviewText = styled(Box).attrs(() => ({ component: Body }))`
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  * {
    display: inline;
    color: currentColor !important;
    background: none !important;
    font-weight: normal !important;
    font-size: inherit !important;
  }
`;

const StyledArrowButton = styled(Button)`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  opacity: 0;
  transition: all ${transition};
  svg {
    display: block;
  }
  &:hover {
    opacity: 1;
    background: ${colors.waterBlue};
    * {
      fill: white;
    }
  }
  ${props =>
    props.isTabbing &&
    `
    &:focus {
      opacity: 1;
      background: ${colors.waterBlue};
      * {
        fill: white;
      }
    }
  `}
`;

const ArrowButton = props => (
  <TabContext.Consumer>
    {isTabbing => <StyledArrowButton {...props} isTabbing={isTabbing} />}
  </TabContext.Consumer>
);

const MoveButtons = styled(Box)`
  &:hover {
    ${StyledArrowButton} {
      opacity: 1 !important;
    }
  }
`;

const BlockHeader = styled(TileHeader)`
  cursor: grab;
`;

const StyledTile = styled(Tile)`
  color: ${colors.greyishBrown};
  font-family: ${fonts.sans};
  &:hover {
    ${StyledArrowButton} {
      opacity: 0.25;
    }
  }
`;

const SortableHeader = SortableHandle(Box);

const SortableItem = SortableElement(Box);

const SortableGrid = SortableContainer(Box);

const deleteHandler = (e, callback?: Function) => {
  e.stopPropagation();
  if (callback) {
    callback();
  }
};

const checkShallow = (a: mixed, b: mixed): boolean => a === b;

const checkDeep = (a: mixed, b: mixed): boolean => a === b || deepEqual(a, b);

const blockItemEqual = (pp, np) => {
  const shallowChecks = [
    'index',
    'hasError',
    'valueLength',
    'inputId',
    'name',
    'placeholder',
    'locale',
    'subsiteId',
    'isActive'
  ];
  const deepChecks = ['value'];
  return (
    shallowChecks.every(p => checkShallow(pp[p], np[p])) &&
    deepChecks.every(p => checkDeep(pp[p], np[p]))
  );
};

const blockItemHeaderEqual = (pp, np) =>
  deepEqual(pp.value, np.value) &&
  ['isActive', 'hasError', 'valueLength', 'minInputs'].every(p =>
    checkShallow(pp[p], np[p])
  );

const BlockItemHeader = React.memo(
  ({
    isActive,
    hasError,
    toggle,
    blockType,
    value,
    valueLength,
    removeInputClicked,
    minInputs
  }: {
    isActive: boolean,
    hasError: boolean,
    toggle: () => void,
    value: Object,
    valueLength: number,
    removeInputClicked: number => void,
    minInputs: number,
    blockType: BlockType
  }) => (
    <BlockHeader isActive={isActive} hasError={hasError} onClick={toggle}>
      <Box display="flex" width="100%" alignItems="center" position="relative">
        <Box flex="0 0 auto" mr={24}>
          <CmsIcon
            name={blockType.toLowerCase().replace(/_/g, '-')}
            width={30}
            height={30}
            display="block"
            fill="currentColor"
          />
        </Box>
        <Preview value={value} type={blockType} />
        <Box display="flex" alignItems="center">
          <ShowConfirmationModal
            data-qa-id="remove-block-modal"
            type="danger"
            title={getText('Remove Block')}
            buttonText={getText('Remove')}
            body={getText('Are you sure you want to remove this block?')}
            onConfirm={wrapPromise(removeInputClicked)}
            render={({ showModal: showConfirmationModal }) => (
              <DeleteButton
                data-qa-id="remove-block"
                disabled={valueLength === minInputs}
                onClick={e => deleteHandler(e, showConfirmationModal)}
                isActive={isActive}
                title={sprintf(getText('Delete %(name)s'), {
                  name: PAGE_BLOCK_OPTIONS[value.type]
                })}
              />
            )}
          />
          <Box ml={[0, 12]} mr={[-12, 0]}>
            {hasError ? (
              <Box
                borderRadius="50%"
                width={40}
                height={40}
                display="flex"
                justifyContent="center"
                alignItems="center"
                color={colors.scarlet}
                bg="white"
              >
                <H4 tag="div">!</H4>
              </Box>
            ) : (
              <ExpandButton
                display="block"
                onClick={toggle}
                data-qa-id="toggle-block"
                isActive={isActive}
                title={sprintf(getText('%(toggle)s %(name)s'), {
                  toggle: isActive ? 'Collapse' : 'Expand',
                  name: PAGE_BLOCK_OPTIONS[value.type]
                })}
              />
            )}
          </Box>
        </Box>
      </Box>
    </BlockHeader>
  ),
  blockItemHeaderEqual
);

const BlockBody = props => {
  const {
    blockType,
    index,
    qaId,
    isActive,
    inputId,
    name,
    placeholder,
    locale,
    subsiteId,
    forSidebars,
    value
  } = props;

  const Block = BLOCK_TYPES[blockType];
  const blockLabel = PAGE_BLOCK_OPTIONS[blockType];
  const body = useMemo(
    () => (
      <Box height={isActive ? 'auto' : 0} overflow={isActive ? '' : 'hidden'}>
        <TileBody>
          <HeadingSmall opacity="0.5">{blockLabel} Block</HeadingSmall>
          <Block
            inputId={inputId}
            name={name}
            data-qa-id={`${qaId}`}
            index={index}
            placeholder={placeholder}
            subsiteId={subsiteId}
            locale={locale}
            forSidebars={forSidebars}
            value={value}
          />
        </TileBody>
      </Box>
    ),
    [
      isActive,
      blockLabel,
      inputId,
      name,
      qaId,
      index,
      placeholder,
      subsiteId,
      locale,
      forSidebars,
      value
    ]
  );
  return body;
};

const BlockItem = ({
  index,
  qaId,
  hasError,
  isActive,
  value,
  valueLength,
  minInputs,
  inputId,
  removeInputClicked,
  moveInputDownClicked,
  moveInputUpClicked,
  toggle,
  name,
  placeholder,
  locale,
  subsiteId,
  blockType,
  forSidebars,
  pageType
}: {
  index: number,
  qaId: string,
  hasError: boolean,
  isActive: boolean,
  value: mixed,
  valueLength: number,
  minInputs: number,
  inputId: ?string,
  removeInputClicked: number => void,
  moveInputDownClicked: number => void,
  moveInputUpClicked: number => void,
  toggle: () => void,
  name: string,
  placeholder: string,
  subsiteId: ?string,
  blockType: BlockType,
  locale: string,
  forSidebars?: boolean,
  pageType?: PageType
}) => {
  const moveButtons = useMemo(
    () => (
      <MoveButtons
        position="absolute"
        right="100%"
        top="56px"
        pr="12px"
        transform="translate(0, -50%)"
      >
        <Box mb="10px">
          {index !== 0 && (
            <ArrowButton onClick={moveInputUpClicked} data-qa-id="uparrow">
              <Box transform="rotate(180deg)">
                <Icon name="chevron" width="12px" />
              </Box>
            </ArrowButton>
          )}
        </Box>
        <Box>
          {index !== valueLength - 1 && (
            <ArrowButton onClick={moveInputDownClicked} data-qa-id="downarrow">
              <Icon
                name="chevron"
                width="12px"
                fill={`${colors.battleshipGrey}`}
              />
            </ArrowButton>
          )}
        </Box>
      </MoveButtons>
    ),
    [moveInputUpClicked, moveInputDownClicked, index, valueLength]
  );

  return (
    <StyledTile position="relative">
      <SortableHeader>
        <BlockItemHeader
          isActive={isActive}
          hasError={hasError}
          toggle={toggle}
          value={value}
          blockType={blockType}
          valueLength={valueLength}
          removeInputClicked={removeInputClicked}
          minInputs={minInputs}
        />
      </SortableHeader>
      {moveButtons}
      <BlockBody
        index={index}
        qaId={qaId}
        hasError={hasError}
        isActive={isActive}
        valueLength={valueLength}
        minInputs={minInputs}
        inputId={inputId}
        removeInputClicked={removeInputClicked}
        moveInputDownClicked={moveInputDownClicked}
        moveInputUpClicked={moveInputUpClicked}
        toggle={toggle}
        name={name}
        placeholder={placeholder}
        locale={locale}
        subsiteId={subsiteId}
        blockType={blockType}
        forSidebars={forSidebars}
        pageType={pageType}
        value={value}
      />
    </StyledTile>
  );
};

const BlockItemMemo = React.memo(BlockItem, blockItemEqual);

type OwnProps = {
  id?: string,
  name: string,
  values: Array<Object>,
  errors?: APIErrors,
  minInputs: number,
  maxInputs?: number,
  updateValueWith: Function => void,
  type?: string,
  placeholder?: string,
  forSidebars: boolean,
  forScheduledContent: boolean,
  subsiteId?: string,
  locale: string,
  pageType?: PageType,
  initialCurrentSection?: number,
  'data-qa-id': string
};

type Props = OwnProps & {
  showModal: typeof showModalRaw
};

const getId = block => block.id || block.tmpId;

const MultiBlock = ({
  id,
  name,
  values,
  errors = {},
  minInputs = 1,
  maxInputs,
  updateValueWith,
  placeholder,
  showModal,
  forScheduledContent,
  forSidebars,
  subsiteId,
  locale,
  pageType,
  initialCurrentSection,
  'data-qa-id': qaId
}: Props) => {
  const [count, setCount] = useState(values.length);
  const [currentBlock, setCurrentSection] = useState(initialCurrentSection);
  const [[errorMap, mostRecentErrors], setErrorMap] = useState([{}, errors]);

  useEffect(
    () => {
      if (errors !== mostRecentErrors) {
        const errorsByBlock = arrToObject(
          values.map((block, index) => {
            const errorKey = `${name}.${index}.data.`;
            const blockErrors = mapObject(
              filterObject(errors, k => k.includes(errorKey)),
              k => k.replace(errorKey, '')
            );
            return [getId(block), blockErrors];
          })
        );
        setErrorMap([errorsByBlock, errors]);
      }
    },
    [values, errors, name, mostRecentErrors]
  );

  const hasError = (blockId: string) => !objectEmpty(errorMap[blockId]);

  const toggle = useCallback(
    (blockId: string) => {
      setCurrentSection(blockId === currentBlock ? null : blockId);

      if (currentBlock !== blockId) {
        const el = document.getElementById(`block-${blockId}`);
        if (el) {
          setTimeout(() => {
            scrollIntoView(el);
          }, 100);
        }
      }
    },
    [currentBlock]
  );

  const updateBlocks = ({ oldIndex, newIndex }) => {
    updateValueWith(
      newIndex >= 0
        ? v => moveArrayItem(v, oldIndex, newIndex)
        : v => removeArrayIndex(v, oldIndex)
    );
  };

  useEffect(
    () => {
      if (values.length !== count) {
        setCount(values.length);
        if (values.length && values.length > count) {
          toggle(getId(values[values.length - 1]));
        }
      }
    },
    [values, toggle, count]
  );

  const sortHandler = ({
    oldIndex,
    newIndex
  }: {
    oldIndex: number,
    newIndex: number
  }) => {
    updateBlocks({ oldIndex, newIndex });
  };

  const moveInputUpClicked = index => () => {
    updateBlocks({ oldIndex: index, newIndex: index - 1 });
  };
  const moveInputDownClicked = index => () => {
    updateBlocks({ oldIndex: index, newIndex: index + 1 });
  };

  const removeInputClicked = blockId => () => {
    updateValueWith(vs => remove(vs, v => getId(v) === blockId));
  };

  const addBlockType = type => {
    // tmpId assigns stable react keys to unsaved blocks to reorder / delete elements
    const newValue = {
      type,
      data: blockDefaultData(type, subsiteId),
      tmpId: uuid()
    };
    updateValueWith(v => v.concat([newValue]));
  };

  const addInputClicked = () => {
    showModal('SELECT_CONTENT_BLOCK', {
      addBlockType,
      blockOptions: forScheduledContent
        ? SCHEDULED_CONTENT_BLOCK_OPTIONS
        : forSidebars
        ? SIDEBAR_BLOCK_OPTIONS
        : PAGE_BLOCK_OPTIONS
    });
  };
  const inputId = index => {
    if (!id) return undefined;
    return index === 0 ? id : `${id}-${index + 1}`;
  };

  const toggleFn = useCallback(key => () => toggle(key), [toggle]);

  return (
    <>
      <SortableGrid
        data-qa-id={qaId}
        data-qa-group-count={values.length}
        onSortEnd={sortHandler}
        distance={5}
        useDragHandle
        useWindowAsScrollContainer
      >
        {values.map((value, index) => {
          const key = getId(value);
          const blockHasError = hasError(key);
          const isActive = key === currentBlock || blockHasError;
          const qaIdItem = `${qaId}-${index}`;
          return (
            <SortableItem
              index={index}
              data-qa-id={qaIdItem}
              id={`block-${key}`}
              data-qa-block-type={value.type}
              my={gutterWidth / 2}
              mt={index === 0 && 0}
              key={key}
            >
              <BlockItemMemo
                index={index}
                isActive={isActive}
                hasError={blockHasError}
                blockType={value.type}
                value={value}
                valueLength={values.length}
                minInputs={minInputs}
                name={name}
                subsiteId={subsiteId}
                locale={locale}
                placeholder={placeholder || ''}
                inputId={inputId(index)}
                toggle={toggleFn(key)}
                moveInputUpClicked={moveInputUpClicked(index)}
                moveInputDownClicked={moveInputDownClicked(index)}
                removeInputClicked={removeInputClicked(key)}
                qaId={qaIdItem}
                forSidebars={forSidebars}
                pageType={pageType}
              />
            </SortableItem>
          );
        })}
      </SortableGrid>
      {values.length > 0 && <Divider my="48px" />}
      {(!maxInputs || (maxInputs > 0 && count < maxInputs)) && (
        <Button
          disabled={maxInputs && values.length >= maxInputs}
          data-qa-id={`${qaId}-add`}
          onClick={addInputClicked}
          width="100%"
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="120px"
            bg="#f5f5f5"
            borderRadius="12px"
            boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
          >
            <H3 color={colors.waterBlue} textAlign="center" m="0">
              {/* TRANSLATORS: Form for adding a content block in CMS */ getText(
                '+ Add More Content'
              )}
            </H3>
          </Box>
        </Button>
      )}
    </>
  );
};

export default connect<Props, OwnProps, _, _, _, _>(
  null,
  { showModal: showModalRaw }
)(MultiBlock);
